import {useEffect, useLayoutEffect, useRef} from 'react';
import ArrTextField from '../../../../common/arr-text-field/ArrTextField';

/** Covers subpixel line-box rounding after browser zoom. */
const HEIGHT_BUFFER_PX = 2;

export interface AutoGrowTextFieldProps {
  value: string;
  placeholder: string;
  dataTestId: string;
  multiline?: boolean;
  className?: string;
  ariaLabel?: string;
  onChange: (value: string) => void;
}

export const toSpokenMultilineValue = (value: string | undefined | null) =>
  (value ?? '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .join('. ');

const AutoGrowTextField = ({
  value,
  placeholder,
  dataTestId,
  multiline = true,
  className,
  ariaLabel,
  onChange,
}: AutoGrowTextFieldProps) => {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const accessibleName = ariaLabel ?? placeholder;
  const fieldValue = value ?? '';
  const spokenValue = toSpokenMultilineValue(fieldValue);
  const hasSpokenValue = /\r?\n/.test(fieldValue) && spokenValue.length > 0;

  const resizeRef = useRef<() => void>(() => undefined);
  resizeRef.current = () => {
    const field = inputRef.current;
    if (field instanceof HTMLTextAreaElement) {
      field.style.height = 'auto';
      field.style.height = `${field.scrollHeight + HEIGHT_BUFFER_PX}px`;
    }
  };

  useLayoutEffect(() => {
    resizeRef.current();
  }, [value]);

  useEffect(() => {
    const handleResize = () => resizeRef.current();
    window.addEventListener('resize', handleResize);
    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      viewport?.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <ArrTextField
      sizeVariant="medium"
      fullWidth
      multiline={multiline}
      rows={multiline ? 1 : undefined}
      minRows={multiline ? 1 : undefined}
      className={className}
      placeholder={placeholder}
      aria-label={accessibleName}
      value={fieldValue}
      inputRef={inputRef}
      onChange={(event) => onChange(event.target.value)}
      onInput={() => resizeRef.current()}
      dataTestId={dataTestId}
      slotProps={{
        htmlInput: {
          'aria-label': accessibleName,
          'aria-description': hasSpokenValue ? spokenValue : undefined,
        },
      }}
    />
  );
};

export default AutoGrowTextField;
